import React, { useState, useRef, memo } from "react";

import classNames from "classnames";

import { ClickableContentWrapper, Icon, IconVariant, theme } from "studio-design-system";

import { ChevronArrowDown } from "~/components/browse/icons";
import { useUser } from "~/hooks/auth/useAuth";
import { useIntercomFeature } from "~/hooks/support/useIntercomFeature";
import { getDoesUserHaveActiveClasses, getDoesUserHaveClasses } from "~/util/user-class-info-utils";
import { getUserFullName } from "~/util/userUtils";

import { ProfilePic } from "../ProfilePic";
import { ProfileMenu, ProfileMenuLink, ProfileMenuSeparator } from "./ProfileMenu";
import * as profileMenuStyles from "./ProfileMenu.css";
import * as styles from "./ProfilePill.css";

interface Props {
  isLoading?: boolean;
  className?: string;
  onLogout?: () => void;
  darkTheme?: boolean;
  size?: number;
}

/**
 * Pro-pic and associated pop-up to render in header for logged in users
 */
const ProfilePill = ({
  className,
  onLogout,
  isLoading,
  darkTheme,
  size,
}: Props): React.ReactElement => {
  const [isInfoMenuOpen, setIsMenuOpen] = useState(false);
  const profileCircleRef = useRef(null);
  const user = useUser();
  const { onQuestionClick } = useIntercomFeature();

  if (!user) {
    return <></>;
  }

  const toggleInfoMenuOpen = () => setIsMenuOpen((current) => !current);
  const onContactSupportClick = () => {
    toggleInfoMenuOpen();
    onQuestionClick();
  };

  return (
    <div className={classNames(styles.profilePill, className)} ref={profileCircleRef}>
      <div className={styles.profilePillContainer} onClick={toggleInfoMenuOpen}>
        <ProfilePic
          size={size || 36}
          userId={user.userId}
          firstName={user.firstName}
          imageUrl={user.profilePhoto}
          isLoading={isLoading}
          gradient
        />
        <ChevronArrowDown />
      </div>
      <ProfileMenu
        darkTheme={darkTheme}
        isOpen={isInfoMenuOpen}
        onOutsideClick={() => setIsMenuOpen(false)}
        exceptions={[profileCircleRef]}
      >
        <div className={profileMenuStyles.profileMenuUserInfo}>
          <span
            className={
              darkTheme ? profileMenuStyles.darkProfileMenuName : profileMenuStyles.profileMenuName
            }
          >
            {getUserFullName(user)}
          </span>
          <span
            className={
              darkTheme
                ? profileMenuStyles.darkProfileMenuEmail
                : profileMenuStyles.profileMenuEmail
            }
          >
            {user.email}
          </span>
        </div>
        <ProfileMenuSeparator darkTheme={darkTheme} />

        {getDoesUserHaveActiveClasses(user) && (
          <>
            <ProfileMenuLink
              darkTheme={darkTheme}
              data-cy="my-class-button"
              pathname={"/v2/dashboard"}
              label={"My Active Classes"}
            />
            <ProfileMenuSeparator darkTheme={darkTheme} />
          </>
        )}

        <ProfileMenuLink pathname="/settings" label="Account Settings" darkTheme={darkTheme} />

        <ProfileMenuSeparator darkTheme={darkTheme} />
        <ClickableContentWrapper
          onClick={onContactSupportClick}
          className={
            darkTheme
              ? profileMenuStyles.darkProfileMenuButton
              : profileMenuStyles.profileMenuButton
          }
        >
          <span className={profileMenuStyles.profileMenuLinkText}>Contact Support</span>
        </ClickableContentWrapper>
        <ProfileMenuSeparator darkTheme={darkTheme} />

        {getDoesUserHaveClasses(user) && (
          <>
            <ProfileMenuLink
              data-cy="my-class-button"
              pathname={"/history"}
              label={"My History"}
              darkTheme={darkTheme}
            />
            <ProfileMenuSeparator darkTheme={darkTheme} />
          </>
        )}

        <ClickableContentWrapper
          onClick={onLogout}
          className={
            darkTheme
              ? profileMenuStyles.darkProfileMenuButton
              : profileMenuStyles.profileMenuButton
          }
        >
          <span className={profileMenuStyles.profileMenuLogoutText}>Log Out</span>
        </ClickableContentWrapper>
      </ProfileMenu>
    </div>
  );
};

export default memo(ProfilePill);
